/**
 * ESimSelectionCombobox — Quick Tests step-2 eSIM picker.
 *
 * One searchable control covering every way to bind a run to a SIM:
 *   • Groups         — the allocator picks any available member at run time
 *   • Specific SIMs  — this exact profile (org inventory, grouped or not)
 *   • SimCheck Shared — a granted super-admin pool, or one exact SIM inside it
 *
 * A flat grouped list (rather than "pick a group, then narrow inside it") is
 * required because `ESimProfile.groupId` is optional — ungrouped profiles are
 * legal and would otherwise be unreachable. Search matters for the same reason
 * the network picker needs it: names repeat and ICCIDs are 19-20 digits, so a
 * native <select> stops working past a couple dozen SIMs.
 *
 * LOCATION RELEVANCE is applied to shared SIMs only, and that asymmetry is
 * deliberate:
 *   • A shared SIM is resident and never downloaded — the allocator hard-pins
 *     its holder modem, so one living at another location can NEVER serve the
 *     selected one (`QueueService._resolveEsimPlacement` → `action: 'fail'`).
 *     Same for a whole pool: `resolveSharedPoolProfile` only considers members
 *     resident on an ONLINE device at the requested location and otherwise
 *     returns NO_SHARED_ESIM. Both are surfaced here instead of at dispatch.
 *   • An org SIM re-downloads onto whichever modem is allocated, so residency
 *     only affects how long the run takes. Those are sorted, never filtered —
 *     hiding them would remove legitimate choices.
 *
 * Emits the encoded selection value documented in utils/esimSelection.ts.
 */
import { Fragment, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, CreditCard, ChevronDown, Layers, Loader2, Share2 } from 'lucide-react';
import type { ESimGroup, ESimProfile } from '@simcheck/shared-types';
import { DEFAULT_SHARED_ESIM_GROUP_ID, sharedPoolSentinel } from '@simcheck/shared-types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  SHARED_PREFIX,
  encodeProfileSelection,
  parseESimSelection,
} from '@/utils/esimSelection';

export interface SharedGroupOption {
  groupId: string;
  groupName: string;
}

export interface ESimSelectionComboboxProps {
  /** Encoded selection (see utils/esimSelection.ts). */
  value: string | null;
  onChange: (value: string | null) => void;
  groups: ESimGroup[];
  /** Org-owned profiles (shared-inventory profiles are excluded internally). */
  profiles: ESimProfile[];
  /** Granted shared pools, already filtered by the org's per-group grant. */
  sharedGroups: SharedGroupOption[];
  /**
   * Members of those pools. Pass them ALL — including `in_use` ones, which are
   * shown with a note rather than dropped, so a pool's count doesn't silently
   * shrink while a test is running.
   */
  sharedProfiles?: ESimProfile[];
  /** Locations the run will target. Empty = nothing to judge relevance against. */
  selectedLocationIds?: string[];
  /** Name of the single selected location, when exactly one is picked. */
  selectedLocationLabel?: string | null;
  /**
   * Where a SIM physically lives right now, or null when it isn't installed on
   * any modem the caller knows about. Drives shared-SIM relevance and the
   * org-SIM sort.
   */
  residencyOf?: (profile: ESimProfile) => { locationId: string | null; locationName: string | null } | null;
  loading?: boolean;
  disabled?: boolean;
  /**
   * Offer an explicit "no eSIM binding" row that emits `null`. Quick Tests
   * requires a selection; the /tests wizard doesn't (its `auto` / `none` modes
   * both persist as `{mode:'none'}` — the run uses whatever profile is already
   * active on the allocated modem).
   */
  clearable?: boolean;
  clearLabel?: string;
  clearDescription?: string;
  /**
   * Short right-aligned hint for a specific org SIM — where it currently lives,
   * or why it can't be used. Lets the picker show residency ("at Tel Aviv")
   * before the user commits, since a SIM can only run where its eUICC is.
   */
  profileNote?: (profile: ESimProfile) => string | null;
}

/** Statuses that can never produce a run — shown, but not selectable. */
const UNUSABLE_STATUSES = new Set(['expired', 'disabled']);

/** Last 4 of the ICCID, enough to disambiguate SIMs that share a name. */
function iccidTail(profile: ESimProfile): string | null {
  const iccid = profile.iccid?.trim();
  if (!iccid) return null;
  return iccid.length > 4 ? `…${iccid.slice(-4)}` : iccid;
}

/** Secondary line for a profile row: group · network · ICCID tail. */
function profileMeta(profile: ESimProfile): string {
  return [profile.groupName || 'No group', profile.networkName, iccidTail(profile)]
    .filter(Boolean)
    .join(' · ');
}

function matchesQuery(profile: ESimProfile, q: string): boolean {
  if (!q) return true;
  return [
    profile.name,
    profile.groupName,
    profile.networkName,
    profile.iccid,
    profile.msisdn,
  ].some((field) => field?.toLowerCase().includes(q));
}

export function ESimSelectionCombobox({
  value,
  onChange,
  groups,
  profiles,
  sharedGroups,
  sharedProfiles = [],
  selectedLocationIds = [],
  selectedLocationLabel = null,
  residencyOf,
  loading = false,
  disabled = false,
  clearable = false,
  clearLabel = 'No specific eSIM',
  clearDescription,
  profileNote,
}: ESimSelectionComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** Reveal shared SIMs that can't serve the selected location(s). */
  const [showOffLocation, setShowOffLocation] = useState(false);

  const parsed = parseESimSelection(value);

  const q = query.trim().toLowerCase();

  // No location picked yet → judge nothing; every SIM is "relevant".
  const locationScoped = selectedLocationIds.length > 0;
  const isAtSelectedLocation = (profile: ESimProfile): boolean => {
    if (!locationScoped) return true;
    const locId = residencyOf?.(profile)?.locationId;
    return !!locId && selectedLocationIds.includes(locId);
  };

  // Org SIMs: resident-at-target first (they run in place instead of paying for
  // a re-download), then alphabetical. Sorted, never filtered — see file header.
  const orgProfiles = useMemo(() => {
    return profiles
      .filter((p) => !p.shared)
      .sort((a, b) => {
        const rank = Number(isAtSelectedLocation(b)) - Number(isAtSelectedLocation(a));
        return rank !== 0 ? rank : (a.name || '').localeCompare(b.name || '');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, selectedLocationIds, residencyOf]);

  // Shared pools with their members attached, split by whether each member can
  // actually serve the selected location(s).
  const sharedPools = useMemo(() => {
    const members = new Map<string, ESimProfile[]>();
    for (const p of sharedProfiles) {
      const gid = p.sharedGroupId || DEFAULT_SHARED_ESIM_GROUP_ID;
      const bucket = members.get(gid);
      if (bucket) bucket.push(p);
      else members.set(gid, [p]);
    }
    return sharedGroups.map((g) => {
      const all = (members.get(g.groupId) || []).sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      );
      return {
        ...g,
        here: all.filter(isAtSelectedLocation),
        elsewhere: locationScoped ? all.filter((p) => !isAtSelectedLocation(p)) : [],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedGroups, sharedProfiles, selectedLocationIds, residencyOf]);

  const filtered = useMemo(() => {
    const matchedGroups = q
      ? groups.filter((g) => g.name?.toLowerCase().includes(q))
      : groups;
    const matchedProfiles = orgProfiles.filter((p) => matchesQuery(p, q));
    // A query searches shared members too — a pool stays listed when its own
    // name matches OR any member does, so searching an ICCID always finds it.
    const matchedShared = sharedPools
      .map((pool) => {
        if (!q) return pool;
        const poolMatches = `simcheck shared ${pool.groupName}`.toLowerCase().includes(q);
        return poolMatches
          ? pool
          : {
              ...pool,
              here: pool.here.filter((p) => matchesQuery(p, q)),
              elsewhere: pool.elsewhere.filter((p) => matchesQuery(p, q)),
            };
      })
      .filter((pool) => !q || pool.here.length > 0 || pool.elsewhere.length > 0 ||
        `simcheck shared ${pool.groupName}`.toLowerCase().includes(q));
    return { matchedGroups, matchedProfiles, matchedShared };
  }, [groups, orgProfiles, sharedPools, q]);

  const offLocationCount = filtered.matchedShared.reduce((n, p) => n + p.elsewhere.length, 0);
  // While searching, off-location matches are shown inline (disabled, with the
  // reason) — a search that finds nothing because of a collapsed section reads
  // as "this SIM doesn't exist".
  const revealOffLocation = showOffLocation || !!q;

  /** "at Tel Aviv" with one location picked, "here" with several. */
  const whereLabel = selectedLocationLabel ? `at ${selectedLocationLabel}` : 'here';

  const totalShown =
    filtered.matchedGroups.length + filtered.matchedProfiles.length + filtered.matchedShared.length;

  // Label for the closed trigger. Resolved from the loaded lists; falls back to
  // a neutral string while they're still loading so a restored selection never
  // renders as "Select an eSIM…".
  const selectedLabel = useMemo(() => {
    if (!value) return null;
    if (parsed.groupId) {
      const group = groups.find((g) => g.id === parsed.groupId);
      return group
        ? { icon: 'group' as const, primary: group.name, secondary: 'any available SIM' }
        : { icon: 'group' as const, primary: 'eSIM group', secondary: null };
    }
    if (parsed.isSharedPool) {
      const gid = value.slice(SHARED_PREFIX.length);
      const match = sharedGroups.find((g) => sharedPoolSentinel(g.groupId) === gid);
      return {
        icon: 'shared' as const,
        primary: `SimCheck Shared — ${match?.groupName || 'pool'}`,
        secondary: 'any available SIM',
      };
    }
    // A specific SIM can be org inventory or one exact member of a shared pool.
    const profile =
      orgProfiles.find((p) => p.id === parsed.profileId) ||
      sharedProfiles.find((p) => p.id === parsed.profileId);
    if (!profile) return { icon: 'profile' as const, primary: 'Specific SIM', secondary: null };
    return {
      icon: profile.shared ? ('shared' as const) : ('profile' as const),
      primary: profile.name || profile.iccid || 'SIM',
      secondary: profile.shared
        ? [`SimCheck Shared — ${profile.sharedGroupName || profile.sharedGroupId || 'pool'}`, iccidTail(profile)]
            .filter(Boolean)
            .join(' · ')
        : profileMeta(profile),
    };
  }, [value, parsed.groupId, parsed.isSharedPool, parsed.profileId, groups, orgProfiles, sharedProfiles, sharedGroups]);

  const select = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  const TriggerIcon =
    selectedLabel?.icon === 'group' ? Layers : selectedLabel?.icon === 'shared' ? Share2 : CreditCard;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setQuery('');
          setShowOffLocation(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className="w-full justify-between font-normal h-auto min-h-10 py-2"
          id="esim-selection-combobox"
        >
          {selectedLabel ? (
            <span className="flex min-w-0 items-center gap-2 text-left">
              <TriggerIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {selectedLabel.primary}
                {selectedLabel.secondary ? (
                  <span className="text-muted-foreground"> — {selectedLabel.secondary}</span>
                ) : null}
              </span>
            </span>
          ) : clearable && !loading ? (
            <span className="flex min-w-0 items-center gap-2 text-left">
              <CreditCard className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{clearLabel}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {loading ? 'Loading eSIMs…' : 'Select an eSIM group or SIM…'}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search groups, SIM names, ICCID…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading eSIMs…
              </div>
            )}

            {!loading && totalShown === 0 && <CommandEmpty>No eSIMs found.</CommandEmpty>}

            {!loading && clearable && !q && (
              <CommandItem
                value="__clear_esim_selection__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                  setQuery('');
                }}
              >
                <Check className={cn('h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{clearLabel}</span>
                  {clearDescription && (
                    <span className="truncate text-xs text-muted-foreground">{clearDescription}</span>
                  )}
                </span>
              </CommandItem>
            )}

            {!loading && filtered.matchedGroups.length > 0 && (
              <CommandGroup heading="Groups — any available SIM">
                {filtered.matchedGroups.map((group) => (
                  <CommandItem key={group.id} value={group.id} onSelect={() => select(group.id)}>
                    <Check
                      className={cn('h-4 w-4', parsed.groupId === group.id ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="truncate">{group.name}</span>
                    <span className="ml-auto pl-2 text-xs text-muted-foreground whitespace-nowrap">
                      {group.profileCount || 0} profile{(group.profileCount || 0) !== 1 ? 's' : ''}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!loading && filtered.matchedProfiles.length > 0 && (
              <CommandGroup heading="Specific SIMs">
                {filtered.matchedProfiles.map((profile) => {
                  const unusable = UNUSABLE_STATUSES.has(profile.status);
                  const encoded = encodeProfileSelection(profile.id);
                  return (
                    <CommandItem
                      key={profile.id}
                      value={encoded}
                      disabled={unusable}
                      onSelect={() => !unusable && select(encoded)}
                    >
                      <Check
                        className={cn(
                          'h-4 w-4',
                          parsed.profileId === profile.id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{profile.name || profile.iccid}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          {profileMeta(profile)}
                        </span>
                      </span>
                      {(() => {
                        const note = unusable
                          ? profile.status.replace(/_/g, ' ')
                          : profileNote?.(profile) ||
                            (profile.status === 'in_use' ? 'in use' : null);
                        return note ? (
                          <span className="ml-auto pl-2 text-xs text-muted-foreground whitespace-nowrap">
                            {note}
                          </span>
                        ) : null;
                      })()}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {!loading && filtered.matchedShared.length > 0 && (
              <CommandGroup heading="SimCheck Shared">
                {filtered.matchedShared.map((pool) => {
                  const encoded = `${SHARED_PREFIX}${sharedPoolSentinel(pool.groupId)}`;
                  // Members are loaded only when the caller passes them; without
                  // that, fall back to the pool row alone rather than claiming 0.
                  const knowsMembers = sharedProfiles.length > 0;
                  // The dispatch-time picker only considers members resident at
                  // the requested location, so a pool with none there can only
                  // fail (NO_SHARED_ESIM). Say so instead of letting it run.
                  const poolDead = knowsMembers && locationScoped && pool.here.length === 0;
                  return (
                    <Fragment key={pool.groupId}>
                      <CommandItem
                        value={encoded}
                        disabled={poolDead}
                        onSelect={() => !poolDead && select(encoded)}
                      >
                        <Check
                          className={cn('h-4 w-4', value === encoded ? 'opacity-100' : 'opacity-0')}
                        />
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate">{pool.groupName}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            any available SIM in this pool
                          </span>
                        </span>
                        {knowsMembers && (
                          <span
                            className={cn(
                              'ml-auto pl-2 text-xs whitespace-nowrap',
                              poolDead ? 'text-destructive' : 'text-muted-foreground',
                            )}
                          >
                            {locationScoped
                              ? `${pool.here.length || 'none'} ${whereLabel}`
                              : `${pool.here.length} SIM${pool.here.length === 1 ? '' : 's'}`}
                          </span>
                        )}
                      </CommandItem>

                      {/* Members of this pool — pick one exact SIM. Selectable
                          because the allocator grant-gates a concrete shared
                          profile per group (GlobalResourceManager byId). */}
                      {[
                        ...pool.here.map((p) => ({ profile: p, offLocation: false })),
                        ...(revealOffLocation
                          ? pool.elsewhere.map((p) => ({ profile: p, offLocation: true }))
                          : []),
                      ].map(({ profile, offLocation }) => {
                        const unusable = UNUSABLE_STATUSES.has(profile.status) || offLocation;
                        const enc = encodeProfileSelection(profile.id);
                        const where = residencyOf?.(profile)?.locationName;
                        const note = UNUSABLE_STATUSES.has(profile.status)
                          ? profile.status.replace(/_/g, ' ')
                          : offLocation
                            ? `at ${where || 'another location'} · can't move`
                            : profile.status === 'in_use'
                              ? 'in use'
                              : where
                                ? `at ${where}`
                                : null;
                        return (
                          <CommandItem
                            key={profile.id}
                            value={enc}
                            disabled={unusable}
                            onSelect={() => !unusable && select(enc)}
                            className="pl-6"
                          >
                            <Check
                              className={cn(
                                'h-4 w-4',
                                parsed.profileId === profile.id ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <span className="flex min-w-0 flex-col">
                              <span className="truncate">{profile.name || profile.iccid}</span>
                              <span className="truncate text-xs text-muted-foreground">
                                {[profile.networkName, iccidTail(profile)].filter(Boolean).join(' · ')}
                              </span>
                            </span>
                            {note && (
                              <span
                                className={cn(
                                  'ml-auto pl-2 text-xs whitespace-nowrap',
                                  offLocation ? 'text-destructive' : 'text-muted-foreground',
                                )}
                              >
                                {note}
                              </span>
                            )}
                          </CommandItem>
                        );
                      })}
                    </Fragment>
                  );
                })}

                {/* Never hide off-location SIMs outright — a missing row reads
                    as missing inventory. Collapse them behind a count instead. */}
                {offLocationCount > 0 && !q && (
                  <CommandItem
                    value="__toggle_off_location__"
                    onSelect={() => setShowOffLocation((s) => !s)}
                    className="text-xs text-muted-foreground"
                  >
                    <ChevronDown
                      className={cn('h-4 w-4 transition-transform', showOffLocation && 'rotate-180')}
                    />
                    {showOffLocation ? 'Hide' : 'Show'} {offLocationCount} shared SIM
                    {offLocationCount === 1 ? '' : 's'} at other locations
                  </CommandItem>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
